import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { apiErrorContent } from '@/components/atoms/ApiErrorMessage';
import { Spinner } from '@/components/atoms/Spinner';
import PageLayout from '@/components/Layout/PageLayout';
import {
    type BioscopeRestartsProps,
    getCurrentBioscopeRestartStep,
    getCurrentBioscopeRouterRestartStep,
} from '@/components/molecules/BioscopeRestarts/BioscopeRestarts';
import { NotFound } from '@/components/molecules/NotFound';
import { ToastInfo, ToastSuccess } from '@/components/molecules/ToastTemplate/ToastTemplate';
import { BioscopeDetail } from '@/components/organisms/BioscopeDetail/BioscopeDetail';
import { BioscopeMovingModalContainer } from '@/components/organisms/MoveBioscopeModal/MoveBioscopeModal';
import { retryFailedQueries } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import { usePermissions } from '@/contexts/auth';
import {
    type BioscopeRestart,
    type BioscopeRouterRestart,
    useBioscopeRestarts,
} from '@/domain/bioscopeRestarts';
import {
    useDeleteBioscope,
    useDeleteLastBioscopeMove,
    useGetBioscopeDetail,
    useUpdateBioscopeSettings,
} from '@/domain/bioscopes';
import { isNotFoundError } from '@/domain/shared/errors';
import { resolveSmsRestartVersion, supportsSmsRestart } from '@/utils/bioscopeVersionHelpers';
import { minutesToMs } from '@/utils/date';
import { isValidPositiveInt } from '@/utils/parsePositiveInt';

const QUERY_TYPES = {
    BIOSCOPE: 'bioscope',
};

interface BioscopeDetailContainerProps {
    bioscopeId: number;
    /** The internal page names the client of each placement. The pen page does not. */
    showPlacementClient?: boolean;
}

function BioscopeDetailContainer({
    bioscopeId,
    showPlacementClient = false,
}: BioscopeDetailContainerProps) {
    const { t } = useTranslation();
    const { isOptoScaleAdmin } = usePermissions();
    const navigate = useNavigate();
    const isValidBioscopeId = isValidPositiveInt(bioscopeId);

    const bioscopeQuery = useGetBioscopeDetail(bioscopeId, {
        enabled: isValidBioscopeId,
        staleTime: minutesToMs(5),
        gcTime: minutesToMs(10),
    });
    const {
        data: bioscope,
        isLoading: isBioscopeLoading,
        refetch: refetchBioscopeDetail,
        error: bioscopeError,
    } = bioscopeQuery;
    // Both deletes run inside a confirmation, which waits for them and shows a failure in
    // the dialog, so they reject rather than report it themselves.
    const { mutateAsync: deleteLastBioscopeMove } = useDeleteLastBioscopeMove({
        onSuccess: () => refetchBioscopeDetail(),
    });

    const { mutateAsync: deleteBioscope } = useDeleteBioscope({
        onSuccess: () => {
            ToastSuccess(t('bioscopeDetail.delete.success'), 'delete-bioscope-success');
            setTimeout(() => {
                navigate('/internal/bioscopes');
            }, 500);
        },
    });
    const [isModalOpenMoveBioscope, setIsModalOpenMoveBioscope] = useState(false);

    const restartsQuery = useBioscopeRestarts(bioscopeId, isValidBioscopeId);
    const {
        restarts,
        routerRestarts,
        isRestartsLoading,
        isRouterRestartsLoading,
        error: restartsError,
        restartBioscope,
        restartRouter,
    } = restartsQuery;

    const smsRestartSupported = supportsSmsRestart(
        resolveSmsRestartVersion(bioscope?.version?.version, bioscope?.bioscopeVersion)
    );
    const smsRestartBlockedReason = smsRestartSupported
        ? undefined
        : t('bioscopeDetail.bioscopeDetail.restartOverSmsIsNot');

    const bioscopeRestarts: BioscopeRestartsProps<BioscopeRestart> = {
        stepMessage: getCurrentBioscopeRestartStep,
        lastBioscopeRestarts: restarts,
        loading: isRestartsLoading,
        restartBioscope,
        disabled: !smsRestartSupported,
        disabledReason: smsRestartBlockedReason,
    };

    const bioscopeRouterRestarts: BioscopeRestartsProps<BioscopeRouterRestart> = {
        stepMessage: getCurrentBioscopeRouterRestartStep,
        lastBioscopeRestarts: routerRestarts,
        loading: isRouterRestartsLoading,
        restartBioscope: restartRouter,
        disabled: !smsRestartSupported,
        disabledReason: smsRestartBlockedReason,
    };

    const { mutateAsync: updateBioscope, isLoading: isUpdatingTestBioscope } =
        useUpdateBioscopeSettings({
            onSuccess: () => {
                refetchBioscopeDetail();
                ToastSuccess(t('bioscopeDetail.update.success'), 'update-bioscope-success');
            },
            onError: (error) => {
                ToastInfo({
                    content: apiErrorContent(error, t('bioscopeDetail.update.error')),
                    status: 'error',
                    icon: 'error',
                    options: {
                        toastId: 'update-bioscope-error',
                        position: 'top-right',
                        autoClose: 5000,
                    },
                });
            },
        });

    const handleUpdateBioscopeSettings = async (
        isTestBioscope: boolean,
        description: string | null
    ) => {
        await updateBioscope({ bioscopeId, isTestBioscope, description });
    };

    const handleMoveSuccess = () => {
        refetchBioscopeDetail();
        setIsModalOpenMoveBioscope(false);
    };

    // The route id stands in until the detail has loaded, so the loading and error states
    // keep the same heading.
    const title = t('bioscopeDetail.pageTitle', { id: bioscope?.id ?? bioscopeId });

    // An unknown id answers 403 (404 once the backend says so); either is not found. The
    // not-found state is the whole page: a "Bioscope ID" heading above it would read as if
    // the id were real.
    if (!isValidBioscopeId || isNotFoundError(bioscopeError)) {
        return <NotFound />;
    }

    if (isBioscopeLoading) {
        return (
            <PageLayout title={title}>
                <Spinner size="lg" fullScreen />
            </PageLayout>
        );
    }

    const error = bioscopeError || restartsError;
    if (error) {
        return (
            <PageLayout title={title}>
                <SomethingHappened
                    error={error}
                    {...retryFailedQueries(bioscopeQuery, restartsQuery)}
                />
            </PageLayout>
        );
    }

    return (
        <PageLayout title={title}>
            <BioscopeDetail
                isBioscopeDetailLoading={isBioscopeLoading}
                isOptoScaleAdmin={isOptoScaleAdmin}
                bioscope={bioscope}
                moveBioscope={() => setIsModalOpenMoveBioscope(true)}
                deleteLastBioscopeMove={() => deleteLastBioscopeMove(bioscopeId)}
                deleteBioscope={() => deleteBioscope(bioscopeId)}
                queryTypes={QUERY_TYPES}
                bioscopeRestarts={bioscopeRestarts}
                bioscopeRouterRestarts={bioscopeRouterRestarts}
                onUpdateTestBioscope={isOptoScaleAdmin ? handleUpdateBioscopeSettings : undefined}
                isUpdatingTestBioscope={isUpdatingTestBioscope}
                showPlacementClient={showPlacementClient}
            />

            {bioscope && (
                <BioscopeMovingModalContainer
                    isOpen={isModalOpenMoveBioscope}
                    bioscopeId={bioscopeId}
                    onClose={() => setIsModalOpenMoveBioscope(false)}
                    key={bioscopeId}
                    onMoveSuccess={handleMoveSuccess}
                />
            )}
        </PageLayout>
    );
}

export default BioscopeDetailContainer;
export { BioscopeDetailContainer };
